import React, { useEffect, useState, useCallback, useLayoutEffect, useRef } from "react";
import { ArrowLeft, ArrowRight, X, Sparkles } from "lucide-react";

/**
 * GuidedTour — tur ghidat intuitiv cu spotlight, fără dependențe externe.
 *
 * Prop `steps`: Array de pași în forma:
 *   { selector?: string, title: string, body: string, placement?: "auto"|"top"|"bottom"|"left"|"right", icon?: string }
 *   - Dacă lipsește `selector` → modal centrat (welcome/final).
 *   - Dacă selectorul nu e găsit → pasul e sărit automat.
 *
 * Prop `open`, `onClose` (apelat când userul închide sau termină turul).
 * Prop `onComplete` (apelat doar la final, separat de skip).
 */
export default function GuidedTour({ open, steps, onClose, onComplete }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const containerRef = useRef(null);

  const totalSteps = steps?.length || 0;
  const step = steps?.[idx];

  // Skip steps whose selectors aren't present in the DOM
  const advanceToValid = useCallback((startIdx, direction = 1) => {
    if (!steps) return -1;
    let i = startIdx;
    while (i >= 0 && i < steps.length) {
      const s = steps[i];
      if (!s.selector) return i;
      if (document.querySelector(s.selector)) return i;
      i += direction;
    }
    return -1;
  }, [steps]);

  // When the tour opens, find the first valid step.
  useEffect(() => {
    if (!open) { setReady(false); return; }
    const first = advanceToValid(0, 1);
    if (first < 0) {
      // Niciun pas valid → închide automat
      onClose?.();
      return;
    }
    setIdx(first);
    setReady(true);
  }, [open, advanceToValid, onClose]);

  // Compute bounding rect for the current step's target element.
  useLayoutEffect(() => {
    if (!open || !ready || !step) return;
    const computeRect = () => {
      if (!step.selector) { setRect(null); return; }
      const el = document.querySelector(step.selector);
      if (!el) { setRect(null); return; }
      // Scroll into view if needed
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      // small delay to let scroll settle
      setTimeout(() => {
        const r = el.getBoundingClientRect();
        setRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        });
      }, 350);
    };
    computeRect();
    // Recompute on resize/scroll
    const onResize = () => computeRect();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [open, ready, step, idx]);

  const goNext = () => {
    const next = advanceToValid(idx + 1, 1);
    if (next < 0) {
      onComplete?.();
      onClose?.();
      return;
    }
    setIdx(next);
  };

  const goBack = () => {
    const prev = advanceToValid(idx - 1, -1);
    if (prev < 0) return;
    setIdx(prev);
  };

  const skipAll = () => {
    onComplete?.();
    onClose?.();
  };

  // Keyboard navigation
  useEffect(() => {
    if (!open || !ready) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); skipAll(); }
      else if (e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); goNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goBack(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, ready, idx]);

  if (!open || !ready || !step) return null;

  // ---- positioning of tooltip card ----
  const PAD = 12;        // spotlight padding around target
  const CARD_W = 360;    // tooltip width
  const CARD_GAP = 16;   // gap between spotlight and tooltip
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let tipStyle = {};
  let centerMode = !rect;
  if (centerMode) {
    tipStyle = {
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: `${Math.min(CARD_W + 40, vw - 24)}px`,
    };
  } else {
    // Decide placement: prefer the side with the most room.
    const spaceBelow = vh - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const spaceRight = vw - (rect.left + rect.width);
    const spaceLeft = rect.left;
    let place = step.placement || "auto";
    if (place === "auto") {
      const candidates = [
        { p: "bottom", s: spaceBelow },
        { p: "top", s: spaceAbove },
        { p: "right", s: spaceRight },
        { p: "left", s: spaceLeft },
      ].sort((a, b) => b.s - a.s);
      place = candidates[0].p;
    }
    if (place === "bottom") {
      tipStyle = {
        top: `${rect.top + rect.height + CARD_GAP + PAD}px`,
        left: `${Math.max(12, Math.min(vw - CARD_W - 12, rect.left + rect.width / 2 - CARD_W / 2))}px`,
        width: `${CARD_W}px`,
      };
    } else if (place === "top") {
      tipStyle = {
        bottom: `${vh - rect.top + CARD_GAP + PAD}px`,
        left: `${Math.max(12, Math.min(vw - CARD_W - 12, rect.left + rect.width / 2 - CARD_W / 2))}px`,
        width: `${CARD_W}px`,
      };
    } else if (place === "right") {
      tipStyle = {
        top: `${Math.max(12, Math.min(vh - 200, rect.top + rect.height / 2 - 100))}px`,
        left: `${rect.left + rect.width + CARD_GAP + PAD}px`,
        width: `${CARD_W}px`,
      };
    } else {
      tipStyle = {
        top: `${Math.max(12, Math.min(vh - 200, rect.top + rect.height / 2 - 100))}px`,
        right: `${vw - rect.left + CARD_GAP + PAD}px`,
        width: `${CARD_W}px`,
      };
    }
  }

  const stepNumber = idx + 1;
  const total = totalSteps;

  return (
    <div ref={containerRef} className="fixed inset-0 z-[10000]" data-testid="guided-tour-overlay">
      {/* Backdrop (dim everything) with hole over spotlight via giant box-shadow */}
      {rect ? (
        <div
          className="fixed pointer-events-none"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.78)",
            border: "2px solid #DC2626",
            borderRadius: 12,
            transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            animation: "tourPulse 2s ease-in-out infinite",
          }}
          data-testid="guided-tour-spotlight"
        />
      ) : (
        <div className="fixed inset-0 bg-black/78 pointer-events-auto" />
      )}

      {/* CSS animation keyframes injected inline */}
      <style>{`
        @keyframes tourPulse {
          0%, 100% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.78), 0 0 0 0 rgba(220,38,38,0.7); }
          50% { box-shadow: 0 0 0 9999px rgba(0,0,0,0.78), 0 0 0 12px rgba(220,38,38,0); }
        }
        @keyframes tourCardIn {
          from { opacity: 0; transform: translateY(8px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Tooltip card */}
      <div
        className="fixed bg-gradient-to-br from-zinc-900 to-zinc-950 text-white rounded-xl shadow-2xl border border-red-600/30 overflow-hidden"
        style={{
          ...tipStyle,
          animation: "tourCardIn 0.35s cubic-bezier(0.4, 0, 0.2, 1) both",
        }}
        data-testid="guided-tour-card"
      >
        {/* Header bar with accent */}
        <div className="h-1 bg-gradient-to-r from-red-600 via-red-500 to-amber-500" />
        <div className="p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-amber-400" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                Pas {stepNumber} din {total}
              </span>
            </div>
            <button
              type="button"
              onClick={skipAll}
              className="text-white/40 hover:text-white/90 transition-colors"
              title="Închide turul (sari peste tot)"
              data-testid="guided-tour-close"
            >
              <X size={16} />
            </button>
          </div>
          <h3 className="font-display text-lg font-extrabold leading-tight mb-2 text-white">
            {step.icon ? <span className="mr-1.5">{step.icon}</span> : null}
            {step.title}
          </h3>
          <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{step.body}</p>

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 my-4">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === idx ? "bg-red-500 w-6" : i < idx ? "bg-white/60 w-1.5" : "bg-white/20 w-1.5"
                }`}
              />
            ))}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={skipAll}
              className="text-[11px] font-bold uppercase tracking-wider text-white/40 hover:text-white/80 transition-colors px-2 py-1.5"
              data-testid="guided-tour-skip"
            >
              Sari peste
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={goBack}
                disabled={idx === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-white/15 text-white/80 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed rounded transition-colors"
                data-testid="guided-tour-prev"
              >
                <ArrowLeft size={12} /> Înapoi
              </button>
              <button
                type="button"
                onClick={goNext}
                className="flex items-center gap-1 px-4 py-1.5 text-xs font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white rounded transition-colors shadow-lg shadow-red-600/30"
                data-testid="guided-tour-next"
              >
                {idx === total - 1 ? "Gata 🎉" : "Următor"} <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
